/**
 * History: turns the event log into readable lines ("Ticked Bob on TC Muffins") and rebuilds
 * the ledger as it was at any earlier point. Used by the History screen, the "view as of"
 * slider and, later, Discord and AI summaries.
 */
import { apply, fold } from "./apply.ts";
import type { StoredEvent } from "./events.ts";
import { formatBp, formatCents, microToCents } from "./money.ts";
import { emptyState, type GroupState, type ItemFields, type Settings } from "./types.ts";
import { splitItem } from "./engine/split.ts";
import { receiptIsOpen, receiptKeyParts } from "./receiptLock.ts";

export interface FieldChange {
  field: string;
  before: string;
  after: string;
}

export interface HistoryEntry {
  event: StoredEvent;
  /** One line in plain words. */
  text: string;
  /** Field-by-field before -> after, for edits. */
  changes: FieldChange[];
  /** The item or person this entry is about, for highlighting the right row. */
  itemId: string | null;
  personId: string | null;
  /** The receipt an item change belongs to ("Costco, Oct 12"), for grouping. */
  place?: string | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-12" -> "Oct 12, 2026". */
export function niceDate(iso: string): string {
  const m = MONTHS[Number(iso.slice(5, 7)) - 1];
  return m ? `${m} ${Number(iso.slice(8, 10))}, ${iso.slice(0, 4)}` : iso;
}

const ITEM_LABELS: Record<keyof ItemFields, string> = {
  date: "Date",
  store: "Store",
  itemNo: "Item #",
  name: "Description",
  category: "Category",
  priceCents: "Price",
  couponCents: "Coupon",
  crvCents: "CRV",
  taxBp: "Tax",
  tipBp: "Tip",
  paidWith: "Paid with",
  paidBy: "Paid by",
  customSplit: "Custom split",
  note: "Note",
  receipt: "Receipt",
  receiptId: "Receipt id",
  splitBy: "Custom split by person",
};
/** Kept by id behind the scenes (Miles, 2026-10-28): not listed as changes, only noted. */
const INTERNAL_ITEM_FIELDS = new Set<keyof ItemFields>(["receiptId", "splitBy"]);

/** Builds readable history for a log, oldest first. */
export function buildHistory(groupId: string, events: readonly StoredEvent[]): HistoryEntry[] {
  const out: HistoryEntry[] = [];
  let state = emptyState(groupId);
  for (const e of events) {
    const h = describe(e, state);
    const placeOf = (s: GroupState) => {
      const it = h.itemId ? s.items[h.itemId] : undefined;
      if (!it) return null;
      return `${it.store}${it.receipt ? ` (receipt ${it.receipt})` : ""}, ${niceDate(it.date)}`;
    };
    const was = placeOf(state);
    state = apply(state, e);
    h.place =
      e.type === "receipt.set"
        ? `${e.data.store}${e.data.receipt ? ` (receipt ${e.data.receipt})` : ""}, ${niceDate(e.data.date)}`
        : (placeOf(state) ?? was);
    out.push(h);
  }
  return out;
}

/** The state as it was right after event number `seq` (0 = before anything happened). */
export function stateAt(groupId: string, events: readonly StoredEvent[], seq: number): GroupState {
  return fold(
    groupId,
    events.filter((e) => e.seq <= seq),
  );
}

/** Describes one event, given the state just before it happened. */
export function describe(e: StoredEvent, before: GroupState): HistoryEntry {
  const name = (id: string | null | undefined) =>
    (id && before.people[id]?.name) || (id ? "someone" : "-");
  const entry = (
    text: string,
    changes: FieldChange[] = [],
    itemId: string | null = null,
    personId: string | null = null,
  ): HistoryEntry => ({
    event: e,
    text: e.meta.undoOf ? `Undo: ${text}` : text,
    changes,
    itemId,
    personId,
  });

  switch (e.type) {
    case "group.created":
      return entry(`Created ${e.data.name}`);
    case "import.marked":
      return entry(`Imported from ${e.data.source} (as of ${niceDate(e.data.capturedAt)})`);
    case "settings.updated": {
      if (e.data.after.receiptsOpenByDefault)
        return entry("Locked the receipts already here; new receipts start open");
      if (Object.keys(e.data.after).length === 1 && e.data.after.receiptLocks) {
        const lockChanges = receiptLockChanges(
          e.data.before.receiptLocks,
          e.data.after.receiptLocks,
          before.settings.receiptsOpenByDefault ?? false,
          before,
        );
        const one = lockChanges.length === 1 ? lockChanges[0]! : null;
        return entry(
          one
            ? `${one.after === "locked" ? "Locked" : "Opened"} the receipt ${one.field}`
            : `Locked or opened ${lockChanges.length} receipts`,
          lockChanges,
        );
      }
      const changes = Object.keys(e.data.after).map((k) => {
        const key = k as keyof typeof e.data.after;
        const fmt = (v: unknown) =>
          key === "ownerPersonId"
            ? name(v as string | null)
            : key === "personAccess"
              ? personAccessText(v as Settings["personAccess"], name)
              : Array.isArray(v)
                ? v.join(", ")
                : v && typeof v === "object"
                  ? Object.entries(v)
                      .map(([page, on]) => `${page} ${on ? "shown" : "hidden"}`)
                      .join(", ") || "-"
                  : String(v ?? "-");
        return {
          field: settingLabel(key),
          before: fmt(e.data.before[key]),
          after: fmt(e.data.after[key]),
        };
      });
      return entry(`Changed settings: ${changes.map((c) => c.field).join(", ")}`, changes);
    }
    case "store.set": {
      const { name: store, before: b, after: a } = e.data;
      if (!b && a)
        return entry(
          `Added store preset ${store} (${formatBp(a.taxBp)}${a.tipBp ? ` + ${formatBp(a.tipBp)} tip` : ""}, ${a.category})`,
        );
      if (b && !a) return entry(`Removed store preset ${store}`);
      const changes: FieldChange[] = [];
      if (b && a && b.taxBp !== a.taxBp)
        changes.push({ field: "Tax", before: formatBp(b.taxBp), after: formatBp(a.taxBp) });
      if (b && a && (b.mcc ?? "") !== (a.mcc ?? ""))
        changes.push({ field: "Merchant code", before: b.mcc || "none", after: a.mcc || "none" });
      if (b && a && (b.tipBp ?? 0) !== (a.tipBp ?? 0))
        changes.push({
          field: "Tip",
          before: formatBp(b.tipBp ?? 0),
          after: formatBp(a.tipBp ?? 0),
        });
      if (b && a && b.category !== a.category)
        changes.push({ field: "Category", before: b.category, after: a.category });
      return entry(`Changed store preset ${store}`, changes);
    }
    case "person.added":
      return entry(`Added ${e.data.person.name}`, [], null, e.data.person.id);
    case "person.removed":
      return entry(`Removed ${e.data.person.name}`, [], null, e.data.person.id);
    case "person.updated": {
      const old = name(e.data.personId);
      const changes: FieldChange[] = [];
      if (e.data.after.name !== undefined)
        changes.push({
          field: "Name",
          before: String(e.data.before.name),
          after: e.data.after.name,
        });
      if (e.data.after.color !== undefined)
        changes.push({
          field: "Color",
          before: String(e.data.before.color),
          after: e.data.after.color,
        });
      if (e.data.after.archived !== undefined)
        changes.push({
          field: "Archived",
          before: e.data.before.archived ? "yes" : "no",
          after: e.data.after.archived ? "yes" : "no",
        });
      const text =
        e.data.after.name !== undefined
          ? `Renamed ${old} to ${e.data.after.name}`
          : e.data.after.archived !== undefined
            ? `${e.data.after.archived ? "Archived" : "Unarchived"} ${old}`
            : `Changed ${old}`;
      return entry(text, changes, null, e.data.personId);
    }
    case "item.added": {
      const it = e.data.item;
      const total = microToCents(splitItem(before, it).totalMicro);
      const who = it.ticked.map(name).join(", ");
      return entry(
        `Added ${it.name} (${formatCents(total)}) at ${it.store}, ${niceDate(it.date)}${who ? `, for ${who}` : ""}`,
        [],
        it.id,
      );
    }
    case "item.updated": {
      const it = before.items[e.data.itemId];
      const keys = Object.keys(e.data.after) as (keyof ItemFields)[];
      const changes = keys
        .filter((k) => !INTERNAL_ITEM_FIELDS.has(k))
        .map((k) => ({
          field: ITEM_LABELS[k],
          before: fmtItemField(k, e.data.before[k], name),
          after: fmtItemField(k, e.data.after[k], name),
        }));
      if (!changes.length)
        return entry(
          keys.includes("receiptId")
            ? `Kept ${it?.name ?? "an item"} on its receipt by id`
            : `Kept the custom split on ${it?.name ?? "an item"} by person`,
          [],
          e.data.itemId,
        );
      return entry(
        `Edited ${it?.name ?? "an item"}: ${changes.map((c) => c.field).join(", ")}`,
        changes,
        e.data.itemId,
      );
    }
    case "item.removed":
      return entry(
        `Removed ${e.data.item.name} (${e.data.item.store}, ${niceDate(e.data.item.date)})`,
        [],
        e.data.item.id,
      );
    case "share.set": {
      const it = before.items[e.data.itemId];
      return entry(
        `${e.data.ticked ? "Ticked" : "Unticked"} ${name(e.data.personId)} on ${it?.name ?? "an item"}`,
        [],
        e.data.itemId,
        e.data.personId,
      );
    }
    case "payment.added": {
      const p = e.data.payment;
      const note = p.note ? ` (${p.note})` : "";
      return entry(
        p.cents >= 0
          ? `${name(p.personId)} paid ${formatCents(p.cents)}${note}`
          : `You paid ${name(p.personId)} ${formatCents(-p.cents)}${note}`,
        [],
        null,
        p.personId,
      );
    }
    case "payment.updated": {
      const p = before.payments[e.data.paymentId];
      const labels: Record<string, string> = {
        at: "Date",
        personId: "Person",
        cents: "Amount",
        note: "Note",
      };
      const changes = Object.keys(e.data.after).map((k) => {
        const key = k as keyof typeof e.data.after;
        const fmt = (v: unknown) =>
          key === "cents"
            ? formatCents(Number(v))
            : key === "personId"
              ? name(v as string)
              : String(v ?? "-");
        return {
          field: labels[k] ?? k,
          before: fmt(e.data.before[key]),
          after: fmt(e.data.after[key]),
        };
      });
      return entry(`Edited ${name(p?.personId)}'s payment`, changes, null, p?.personId ?? null);
    }
    case "payment.removed": {
      const p = e.data.payment;
      return entry(
        `Removed ${name(p.personId)}'s ${formatCents(p.cents)} payment`,
        [],
        null,
        p.personId,
      );
    }
    case "catalog.noted": {
      const label = e.data.key.startsWith("#") ? `item ${e.data.key}` : e.data.key.slice(2);
      const kw = (n: { keywords: string[] } | null) =>
        n?.keywords.length ? n.keywords.join(", ") : "none";
      const who = e.data.after?.source === "ai" ? " (from the AI lookup)" : "";
      if (!e.data.after) return entry(`Cleared catalog notes for ${label}`);
      const changes: FieldChange[] = [];
      if (kw(e.data.before) !== kw(e.data.after))
        changes.push({ field: "Keywords", before: kw(e.data.before), after: kw(e.data.after) });
      if ((e.data.before?.icon ?? "") !== (e.data.after.icon ?? ""))
        changes.push({
          field: "Icon",
          before: e.data.before?.icon || "automatic",
          after: e.data.after.icon || "automatic",
        });
      const tx = (t: boolean | null | undefined) =>
        t === true ? "taxed" : t === false ? "not taxed" : "not known";
      if (tx(e.data.before?.taxable) !== tx(e.data.after.taxable))
        changes.push({
          field: "Tax",
          before: tx(e.data.before?.taxable),
          after: tx(e.data.after.taxable),
        });
      const pn = (n: { priceNowCents?: number } | null) =>
        n?.priceNowCents === undefined ? "none" : formatCents(n.priceNowCents);
      if (pn(e.data.before) !== pn(e.data.after))
        changes.push({ field: "Price now", before: pn(e.data.before), after: pn(e.data.after) });
      return entry(`Catalog notes for ${label}${who}`, changes);
    }
    case "receipt.set": {
      const f = (c: number | null) => (c === null ? "none" : formatCents(c));
      return entry(`Receipt total for ${e.data.store}, ${niceDate(e.data.date)}`, [
        { field: "Receipt total", before: f(e.data.before), after: f(e.data.after) },
      ]);
    }
    default: {
      const unhandled: never = e;
      return unhandled;
    }
  }
}

function fmtItemField(
  key: keyof ItemFields,
  value: unknown,
  name: (id: string | null) => string,
): string {
  if (value === null || value === undefined || value === "") {
    if (key === "taxBp" || key === "tipBp") return "store default";
    if (key === "category") return "store default";
    return "-";
  }
  switch (key) {
    case "priceCents":
    case "couponCents":
    case "crvCents":
      return formatCents(Number(value));
    case "taxBp":
    case "tipBp":
      return formatBp(Number(value));
    case "paidBy":
      return name(String(value));
    case "date":
      return niceDate(String(value));
    default:
      return String(value);
  }
}

function settingLabel(key: string): string {
  switch (key) {
    case "ownerPersonId":
      return "Owner";
    case "overdueWarnDays":
      return "Overdue warning (days)";
    case "overdueLateDays":
      return "Overdue late (days)";
    case "categories":
      return "Categories";
    case "cards":
      return "Cards";
    case "memberAccess":
      return "What members can see";
    case "treasurerAccess":
      return "What club treasurers can see";
    case "joining":
      return "Who can join";
    case "personAccess":
      return "What each person can see";
    case "receiptLocks":
      return "Receipts locked";
    case "receiptsOpenByDefault":
      return "New receipts start open";
    default:
      return key;
  }
}

/** One change per receipt whose lock changed: "Costco, Oct 27" open -> locked. */
function receiptLockChanges(
  before: Record<string, boolean> | undefined,
  after: Record<string, boolean>,
  openByDefault: boolean,
  groupState?: GroupState,
): FieldChange[] {
  const out: FieldChange[] = [];
  for (const key of new Set([...Object.keys(before ?? {}), ...Object.keys(after)])) {
    const r = receiptKeyParts(key, groupState);
    const state = (m: Record<string, boolean> | undefined) =>
      receiptIsOpen({ receiptLocks: m, receiptsOpenByDefault: openByDefault }, r)
        ? "open"
        : "locked";
    if (state(before) === state(after)) continue;
    out.push({
      field: `${r.store}${r.receipt ? ` (receipt ${r.receipt})` : ""}, ${niceDate(r.date)}`,
      before: state(before),
      after: state(after),
    });
  }
  return out;
}

/** "Jianing Dong: Receipts hidden, Groceries hidden" for one-person page and category changes. */
function personAccessText(
  v: Settings["personAccess"],
  name: (id: string | null) => string,
): string {
  const LABEL: Record<string, string> = {
    people: "People",
    receipts: "Receipts",
    insights: "Insights",
    history: "History",
    settings: "Settings",
  };
  const parts = Object.entries(v ?? {}).map(([pid, a]) => {
    const bits = [
      ...Object.entries(a.pages ?? {}).map(
        ([k, on]) => `${LABEL[k] ?? k} ${on ? "shown" : "hidden"}`,
      ),
      ...(a.hiddenCategories ?? []).map((c) => `${c} hidden`),
    ];
    return `${name(pid)}: ${bits.join(", ") || "group setting"}`;
  });
  return parts.join("; ") || "-";
}

// ------------------------------------------------------------------ grouping

export interface HistoryGroup {
  /** The newest entry's event id. */
  id: string;
  title: string;
  /** Newest first. */
  entries: HistoryEntry[];
  actor: string;
  /** First and last change times. */
  from: string;
  to: string;
}

const GAP_MS = 10 * 60_000;

function family(h: HistoryEntry): string {
  const t = h.event.type;
  if (h.event.actor === "import") return "import";
  if (t.startsWith("payment.")) return `payment:${h.event.id}`; // payments always stand alone
  if (t.startsWith("item.") || t === "share.set" || t === "receipt.set")
    return h.place ? `place:${h.place}` : "items";
  if (
    t.startsWith("person.") ||
    t === "store.set" ||
    t === "settings.updated" ||
    t === "catalog.noted"
  )
    return "setup";
  return `one:${h.event.id}`;
}

function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

function summarize(entries: HistoryEntry[], fam: string): string {
  if (entries.length === 1) return entries[0]!.text;
  if (fam === "import") return `Imported the Sheet (${entries.length} changes)`;
  const count = (pred: (h: HistoryEntry) => boolean) => entries.filter(pred).length;
  const undo = count((h) => !!h.event.meta.undoOf);
  const parts: string[] = [];
  const add = (n: number, text: string) => n && parts.push(text);
  const ty = (t: string) => (h: HistoryEntry) => h.event.type === t && !h.event.meta.undoOf;
  add(count(ty("item.added")), `added ${plural(count(ty("item.added")), "item")}`);
  add(count(ty("item.updated")), `${plural(count(ty("item.updated")), "edit")}`);
  add(count(ty("item.removed")), `removed ${plural(count(ty("item.removed")), "item")}`);
  add(count(ty("share.set")), `${plural(count(ty("share.set")), "tick")}`);
  add(count(ty("receipt.set")), "set the receipt total");
  add(count(ty("person.added")), `added ${plural(count(ty("person.added")), "person", "people")}`);
  add(
    count(ty("person.updated")) + count(ty("person.removed")),
    `${plural(count(ty("person.updated")) + count(ty("person.removed")), "change")} to people`,
  );
  add(count(ty("store.set")), `${plural(count(ty("store.set")), "store preset")}`);
  add(count(ty("settings.updated")), "settings");
  add(count(ty("catalog.noted")), `${plural(count(ty("catalog.noted")), "catalog note")}`);
  add(undo, `${plural(undo, "undo", "undos")}`);
  const known = parts.length ? parts.join(", ") : plural(entries.length, "change");
  const head = fam.startsWith("place:")
    ? `${fam.slice(6)}: `
    : fam === "setup"
      ? "Group setup: "
      : "";
  const text = `${head}${known}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Groups history the way you'd remember it: one person's run of changes on the same receipt
 * (or the same kind of setup) within a few minutes of each other becomes one line, like
 * "Costco, Oct 12, 2026: added 12 items, 30 ticks". Payments always stand alone. Takes entries
 * oldest first; returns groups newest first.
 */
export function groupHistory(entries: readonly HistoryEntry[], gapMs = GAP_MS): HistoryGroup[] {
  const groups: { fam: string; list: HistoryEntry[] }[] = [];
  for (const h of entries) {
    const fam = family(h);
    const last = groups[groups.length - 1];
    const prev = last?.list[last.list.length - 1];
    if (
      last &&
      prev &&
      last.fam === fam &&
      prev.event.actor === h.event.actor &&
      (fam === "import" || Date.parse(h.event.at) - Date.parse(prev.event.at) <= gapMs)
    ) {
      last.list.push(h);
    } else {
      groups.push({ fam, list: [h] });
    }
  }
  return groups
    .map(({ fam, list }) => ({
      id: list[list.length - 1]!.event.id,
      title: summarize(list, fam),
      entries: list.slice().reverse(),
      actor: list[0]!.event.actor,
      from: list[0]!.event.at,
      to: list[list.length - 1]!.event.at,
    }))
    .reverse();
}
